import { useState, useEffect } from 'react';
import { Plus, Check, Trash2, Clock, AlertCircle, ChevronDown, Tag } from 'lucide-react';
import { getTodos, createTodo, updateTodo, deleteTodo } from '../../services/emailApi';

const ACTION_TYPES = ['reply', 'review', 'read', 'write', 'sign', 'pay', 'schedule', 'research', 'forward', 'call', 'decide'];
const PRIORITIES = ['high', 'medium', 'low'];

const PRIORITY_COLORS = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

const ACTION_ICONS = {
  reply: '↩️', review: '👀', read: '📖', write: '✏️', sign: '✍️',
  pay: '💰', schedule: '📅', research: '🔍', forward: '➡️', call: '📞', decide: '⚖️',
};

export default function TodosView() {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newPriority, setNewPriority] = useState('medium');
  const [newActionType, setNewActionType] = useState('');
  const [newTimeEstimate, setNewTimeEstimate] = useState('');
  const [groupBy, setGroupBy] = useState('priority'); // priority, action_type, status

  useEffect(() => {
    loadTodos();
  }, []);

  async function loadTodos() {
    try {
      const data = await getTodos();
      setTodos(data.todos || []);
    } catch (err) {
      console.error('Failed to load todos:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!newTitle.trim()) return;
    try {
      await createTodo({
        title: newTitle,
        priority: newPriority,
        action_type: newActionType || null,
        time_estimate: newTimeEstimate || null,
      });
      setNewTitle('');
      setNewActionType('');
      setNewTimeEstimate('');
      setShowAdd(false);
      loadTodos();
    } catch (err) {
      console.error('Failed to create todo:', err);
    }
  }

  async function handleToggle(todo) {
    try {
      await updateTodo(todo.id, { is_completed: !todo.is_completed });
      setTodos(prev => prev.map(t => t.id === todo.id ? { ...t, is_completed: !t.is_completed } : t));
    } catch (err) {
      console.error('Failed to update todo:', err);
    }
  }

  async function handleDelete(id) {
    try {
      await deleteTodo(id);
      setTodos(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error('Failed to delete todo:', err);
    }
  }

  const activeTodos = todos.filter(t => !t.is_completed);
  const completedTodos = todos.filter(t => t.is_completed);

  function groupTodos(list) {
    const groups = {};
    for (const todo of list) {
      const key = groupBy === 'priority' ? (todo.priority || 'none')
        : groupBy === 'action_type' ? (todo.action_type || 'other')
        : 'all';
      if (!groups[key]) groups[key] = [];
      groups[key].push(todo);
    }
    return groups;
  }

  const grouped = groupTodos(activeTodos);
  const groupOrder = groupBy === 'priority' ? ['high', 'medium', 'low', 'none'] : Object.keys(grouped).sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold">Todos</h1>
          <p className="text-sm text-gray-500">{activeTodos.length} active, {completedTodos.length} completed</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={groupBy}
            onChange={e => setGroupBy(e.target.value)}
            className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-700 rounded bg-transparent"
          >
            <option value="priority">Group by Priority</option>
            <option value="action_type">Group by Action</option>
            <option value="status">No grouping</option>
          </select>
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
          >
            <Plus size={14} />
            Add
          </button>
        </div>
      </div>

      {/* Add form */}
      {showAdd && (
        <form onSubmit={handleAdd} className="mb-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <input
            type="text"
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            placeholder="What needs to be done?"
            className="w-full px-3 py-2 mb-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-transparent focus:ring-2 focus:ring-blue-500 outline-none"
            autoFocus
          />
          <div className="flex gap-2 flex-wrap">
            <select
              value={newPriority}
              onChange={e => setNewPriority(e.target.value)}
              className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-700 rounded bg-transparent"
            >
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              value={newActionType}
              onChange={e => setNewActionType(e.target.value)}
              className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-700 rounded bg-transparent"
            >
              <option value="">Action type...</option>
              {ACTION_TYPES.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <input
              type="text"
              value={newTimeEstimate}
              onChange={e => setNewTimeEstimate(e.target.value)}
              placeholder="Time estimate"
              className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-700 rounded bg-transparent w-28"
            />
            <button type="submit" className="ml-auto px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
              Add
            </button>
          </div>
        </form>
      )}

      {/* Active todos */}
      {groupOrder.map(group => {
        const items = grouped[group];
        if (!items?.length) return null;
        return (
          <div key={group} className="mb-4">
            {groupBy !== 'status' && (
              <h3 className="text-xs font-semibold uppercase text-gray-500 mb-2 flex items-center gap-1">
                {groupBy === 'priority' && <span className={`inline-block w-2 h-2 rounded-full ${group === 'high' ? 'bg-red-500' : group === 'medium' ? 'bg-yellow-500' : group === 'low' ? 'bg-green-500' : 'bg-gray-400'}`} />}
                {groupBy === 'action_type' && <span>{ACTION_ICONS[group] || '📋'}</span>}
                {group} ({items.length})
              </h3>
            )}
            <div className="space-y-1">
              {items.map(todo => (
                <div
                  key={todo.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 group"
                >
                  <button
                    onClick={() => handleToggle(todo)}
                    className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center hover:border-blue-500 shrink-0"
                  >
                    {todo.is_completed && <Check size={12} className="text-blue-600" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${todo.is_completed ? 'line-through text-gray-400' : ''}`}>
                      {todo.title}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {todo.action_type && (
                        <span className="text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                          {ACTION_ICONS[todo.action_type] || ''} {todo.action_type}
                        </span>
                      )}
                      {todo.time_estimate && (
                        <span className="text-xs text-gray-400 flex items-center gap-0.5">
                          <Clock size={10} />
                          {todo.time_estimate}
                        </span>
                      )}
                      {todo.priority && (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${PRIORITY_COLORS[todo.priority] || ''}`}>
                          {todo.priority}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(todo.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {activeTodos.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <AlertCircle size={32} className="mx-auto mb-2" />
          <p>No active todos</p>
          <p className="text-sm">AI-detected actions from your emails will appear here</p>
        </div>
      )}

      {/* Completed */}
      {completedTodos.length > 0 && (
        <div className="mt-6">
          <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">
            Completed ({completedTodos.length})
          </h3>
          <div className="space-y-1 opacity-60">
            {completedTodos.slice(0, 10).map(todo => (
              <div key={todo.id} className="flex items-center gap-3 px-3 py-2 rounded-lg group">
                <button
                  onClick={() => handleToggle(todo)}
                  className="w-5 h-5 rounded-full border-2 border-green-400 bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0"
                >
                  <Check size={12} className="text-green-600" />
                </button>
                <span className="text-sm line-through text-gray-400 flex-1">{todo.title}</span>
                <button
                  onClick={() => handleDelete(todo.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
